import { emailValidator } from "helpers/emailValidator";
import { useCallback, useState } from "react";
import { useHandleNumberInputOnChangeV2 } from "./useFormActions/onChangeNumberV2";

export const useForm = (initialState = {}, fieldValidations = {}) => {
   const [inputValues, setInputValues] = useState(initialState);
   const [fieldErrors, setFieldErrors] = useState({});

   const handleInputOnChange = useCallback(
      ({ target }, active = null, uppercase = false) => {
         const { name, value } = target;
         const validations = fieldValidations[name] || {};
         const exactLength = validations.exactLength;
         const maxLength = validations.maxLength;
         const minLength = validations.minLength;
         const message = validations.message;
         const isRequired = validations.required;
         const isEmail = validations.isEmail;

         let errorMessage = "";

         if (isRequired && value.trim() === "") {
            errorMessage = "* Este campo es requerido";
         }

         else if (isEmail && value.trim() !== "" && !emailValidator(value)) {
            errorMessage = "* El correo no es válido";
         }

         else if (value.length > 0 && minLength && value.length < minLength) {
            errorMessage = `* Deben ser al menos ${minLength} caracteres`;
         }

         else if (maxLength && value.length > maxLength) {
            errorMessage = message || `* Máximo ${maxLength} caracteres`;
         }

         else if (exactLength && value.length > exactLength) {
            errorMessage = message || `* Debe contener exactamente ${exactLength} caracteres`;
         }

         setFieldErrors({
            ...fieldErrors,
            [name]: errorMessage,
         });

         const trimmedValue = exactLength ? value.substring(0, exactLength) : value;

         setInputValues({
            ...inputValues,
            [name]: uppercase ? trimmedValue.toUpperCase() : trimmedValue,
         });

         if (active) {
            setInputValues({
               ...inputValues,
               [name]: value,
            });
         }
      },
      [inputValues, fieldErrors]
   );



   const handleNumberInputOnChange = useCallback((e, props = {}) => {
      const { name, value, selectionStart } = e.target;

      let raw = value
         .replace(/[^\d.-]/g, '')
         .replace(/(\..*)\./g, '$1');

      const hasNegative = raw.includes('-');
      if (hasNegative) {
         raw = raw.replace(/-/g, '');
         raw = '-' + raw;
      }

      if (props.decimals == 0) {
         raw = raw.split('.')[0];
      }

      const [intPart = '', decimalPart = ''] = raw.split('.');
      const isAddingDecimal = value.endsWith('.') && selectionStart === value.length;

      let formattedValue = raw;
      const shouldFormat = !isAddingDecimal && !raw.endsWith('.');

      if (shouldFormat && intPart) {
         const numericValue = intPart ? parseInt(intPart.replace(/,/g, ''), 10) : 0;
         const formattedInt = numericValue.toLocaleString('en-US');

         formattedValue = hasNegative ? `-${formattedInt}` : formattedInt;

         if (decimalPart) {
            formattedValue += `.${decimalPart}`;
         } else if (raw.includes('.')) {
            formattedValue += '.';
         }
      }

      if (props.decimals != null && decimalPart) {
         const parts = formattedValue.split('.');
         if (parts.length > 1) {
            parts[1] = parts[1].slice(0, props.decimals);
            formattedValue = parts.join('.');
         }
      }

      const diff = formattedValue.length - value.length;
      const newCursor = Math.max(0, (selectionStart || 0) + diff);

      if (props?.justNumber) {
         setInputValues(prev => ({
            ...prev,
            [name]: value.replace(/[^0-9]/g, '')
         })
         )
      } else {
         setInputValues(prev => ({
            ...prev,
            [name]: formattedValue
         }));
      }

      requestAnimationFrame(() => {
         const input = document.querySelector(`input[name="${name}"]`);
         if (input) {
            if (isAddingDecimal && !input.value.includes('.')) {
               input.value = input.value + '.';
            }
            input.setSelectionRange(newCursor, newCursor);
         }
      });
   }, []);

   const handleNumberInputOnChangeV2 = useHandleNumberInputOnChangeV2(setInputValues)

   const cleanForm = () => {
      setInputValues(initialState);
      setFieldErrors({});
   };

   const handleNewValues = (values) => {
      setInputValues(prev => ({
         ...prev,
         ...values
      }));
      setFieldErrors({})
   };

   const patchValues = (values) => {
      setInputValues(prev => ({
         ...prev,
         ...values
      }));
   };

   const handleInputAutoComplete = (e, v) => {
      e.preventDefault()
      setInputValues(prev => ({
         ...prev,
         [e.target.name]: v
      }));
   }

   const checkboxOnChange = (e) => {
      setInputValues(prev => ({
         ...prev,
         [e.target.name]: e.target.checked
      }));
   }

   const runValidates = (optionalFields = []) => {
      const newFieldErrors = {};
      let field = {};
      let inputValue = "";
      for (const fieldName in fieldValidations) {
         field = fieldValidations[fieldName];
         inputValue = inputValues[fieldName];
         if (inputValue?.length > 0 && inputValue?.length < field?.minLength && !optionalFields.includes(fieldName)) {
            newFieldErrors[fieldName] = `* Deben ser al menos ${field?.minLength} caracteres`;
         }
         else if (inputValue?.length > 0 && inputValue?.length > field?.maxLength && !optionalFields.includes(fieldName)) {
            newFieldErrors[fieldName] = `* Máximo ${field?.maxLength} caracteres`;
         }
         else if (
            inputValue?.length > 0
            && (inputValue?.length < field?.exactLength || inputValue?.length > field?.exactLength)
            && !optionalFields.includes(fieldName)
         ) {
            newFieldErrors[fieldName] = field?.message || `* Debe contener exactamente ${field?.exactLength} caracteres`
         }
         else if (field.required && field.isEmail && !optionalFields.includes(fieldName)) {
            if (!Array.isArray(inputValue) &&!emailValidator(inputValues[fieldName])) {
               newFieldErrors[fieldName] = "* El correo no es válido";
            }
            if (Array.isArray(inputValue) && inputValue.length === 0) {
               newFieldErrors[fieldName] = "* Debe agregar al menos un correo";
            }
         }
         else if (field.required && !inputValues[fieldName] && !optionalFields.includes(fieldName)) {
            newFieldErrors[fieldName] = "* Este campo es requerido";
         }
      }
      setFieldErrors(newFieldErrors);
      return Object.keys(newFieldErrors).length > 0;
   };

   const runValidatesExe = (optionalFields = [], action = null) => {
      if (!runValidates(optionalFields)) action && action();
   };


   return {
      inputValues,
      fieldErrors,
      handleInputOnChange,
      handleInputAutoComplete,
      cleanForm,
      handleNewValues,
      setFieldErrors,
      runValidates,
      runValidatesExe,
      handleNumberInputOnChange,
      //new names
      values: inputValues,
      errors: fieldErrors,
      onChange: handleInputOnChange,
      autoOnChange: handleInputAutoComplete,
      checkboxOnChange,
      setValues: handleNewValues,
      patchValues,
      validate: runValidatesExe,
      onChangeNumber: handleNumberInputOnChange,
      reset: cleanForm,
      onChangeNumberV2: handleNumberInputOnChangeV2
   };
};
